/**
 * What this handset remembers about whose it is , and it is one number.
 *
 * **The defect this closes**, found by the founder at `app-staging` on 30 August
 * 2026: an account created through the registration flow could not log in
 * through the login screen. Every PIN was wrong, however correct it was.
 *
 * **The cause was not the PIN.** Both screens cap at four digits, both
 * workflows say four, and the API asserts `^\d{4}$` , the length was never in
 * disagreement. What was missing is the NUMBER. `Connexion` read the phone out
 * of the in-memory session, which is empty on a fresh page load, so it rendered
 * a blank masked number and sent `phone: ""`. The API then looked up a client by
 * an empty phone, found none, and refused , correctly, and with the same
 * sentence a wrong PIN gets, because 05 makes those two refusals identical **so
 * that a refusal is not an oracle on a phone book**. The very property that
 * protects a stranger is what hid this from the person who owned the account.
 *
 * **WF-CLI-02 assumes this memory exists**, and reading it again is what named
 * the fix: its actor is a *"registered client on a recognised device"*, and its
 * step 1 shows the *"masked phone (all digits except the last two replaced by
 * `x`)"*. A screen cannot mask a number it does not have. The workflow was
 * describing a handset that knows whose it is; the app never wrote that down.
 *
 * **The number and NOT the secret.** 4.6.5 puts the client app's state in
 * `localStorage` because *"a client loses his connection mid-form and comes back
 * on the same handset"*, and a phone number is an identifier , the one the
 * product already calls the identifier , not a credential. **The PIN is never
 * stored here and neither is any token**: the PIN lives only in the field a
 * person types it into, and the access token lives in memory and dies with the
 * page. If this file ever grows a second key, that is the line to re-read.
 *
 * **Re-read, and amended: the file grew a second key** (round `PB-2`, `M4`,
 * `P0-IDN-057`). The text above is kept as it was written; this is what changed
 * and why. 06 §6.1.4 binds the PIN to ONE handset, and until this round nothing
 * did: a phone and a PIN opened the account from any handset. The binding is a
 * secret the server issues to this handset when the OTP proves the number, and
 * which it stores only as a hash. **It is stored here**, beside the number,
 * because a handset that cannot remember it is not the handset the PIN is bound
 * to. It is a possession factor and not a credential on its own: without the
 * PIN it opens nothing, and the PIN, the token and the access token are still
 * never stored , that sentence above stands. `Ce n'est pas vous ?` forgets both
 * keys together: the next person on this handset proves their own number and
 * receives their own secret.
 *
 * **Every access is guarded.** A handset with `localStorage` disabled, a private
 * window, a browser that throws on access , each answers "nothing remembered",
 * which routes to the OTP path. A thrown exception here would be a blank screen
 * on the first thing a buyer opens.
 */

const CLE = "hakili.client.numero";
/** `P0-IDN-057`: the secret the server issued to this handset. See the header's amendment. */
const SECRET_KEY = "hakili.client.appareil";

/** The remembered number, or `null` when this handset has never registered. */
export function numeroMemorise(): string | null {
  try {
    const value = globalThis.localStorage.getItem(CLE);
    return value === null || value === "" ? null : value;
  } catch {
    return null;
  }
}

/** Written when a session is opened , registration or login , and never before. */
export function memoriserNumero(phone: string): void {
  if (phone === "") return;
  try {
    globalThis.localStorage.setItem(CLE, phone);
  } catch {
    // A handset that cannot remember is a handset that takes the OTP path next
    // time. That is slower for the buyer and wrong for nobody.
  }
}

/**
 * Forgotten on `Ce n'est pas vous ?`, and on nothing else.
 *
 * **A sign-out does NOT forget the number**, deliberately: WF-CLI-02's whole
 * shape is a person coming back to their own handset and typing four digits.
 * Forgetting on sign-out would send every returning buyer through an OTP they
 * do not need, which is the friction this screen exists to remove.
 */
export function oublierNumero(): void {
  try {
    globalThis.localStorage.removeItem(CLE);
    globalThis.localStorage.removeItem(SECRET_KEY);
  } catch {
    // Nothing to do, and nothing worth failing a screen over.
  }
}

/** The secret this handset was issued, or `undefined` when it holds none , the OTP path then issues one. */
export function handsetSecret(): string | undefined {
  try {
    const value = globalThis.localStorage.getItem(SECRET_KEY);
    return value === null || value === "" ? undefined : value;
  } catch {
    return undefined;
  }
}

/** Written when the server issues a secret , registration, or a known number verified on a new handset. */
export function rememberSecret(secret: unknown): void {
  if (typeof secret !== "string" || secret === "") return;
  try {
    globalThis.localStorage.setItem(SECRET_KEY, secret);
  } catch {
    // A handset that cannot remember its secret takes the OTP path next time, as for the number.
  }
}
